class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
    setTimeout(function() {    
        price_range_filter();
        initVariantSwatches();
          document.querySelectorAll('.view-toggle button').forEach(button => {
            button.addEventListener('click', function () {
              const cols = this.dataset.columns;
              const grid = document.getElementById('product-grid');
              grid.className = `grid product-grid grid--${cols}-col-desktop`;

              // Toggle active state
              document.querySelectorAll('.view-toggle button').forEach(btn => btn.classList.remove('active'));
              this.classList.add('active');
            });
          });
          // COLLECTION GRID VIEW FOR < 630PX 
          const gridElement = document.getElementById('product-grid');
          const viewToggleContainer = document.querySelector('.view-toggle');
          const toggleButtons = viewToggleContainer ? viewToggleContainer.querySelectorAll('button[data-columns]') : [];
          const mediaQuery = window.matchMedia('(max-width: 630px)');
          let currentGridPreference = 3;

          function resetToggleActiveState() {
              toggleButtons.forEach(button => button.classList.remove('active'));
          }

          function updateGridClasses(columns) {
              const classesToRemove = [
                  'grid--1-col-desktop', 
                  'grid--2-col-desktop', 'grid--2-col-tablet-down', 
                  'grid--3-col-desktop', 'grid--3-col-tablet-down', 
                  'grid--4-col-desktop', 'grid--4-col-tablet-down'
              ];
              gridElement.classList.remove(...classesToRemove);

              switch (columns) {
                  case 1:
                      gridElement.classList.add('grid--1-col-desktop');
                      break;
                  case 2:
                      gridElement.classList.add('grid--2-col-tablet-down', 'grid--2-col-desktop');
                      break;
                  case 3:
                      gridElement.classList.add('grid--3-col-tablet-down', 'grid--3-col-desktop');
                      break;
                  case 4:
                      gridElement.classList.add('grid--4-col-tablet-down', 'grid--4-col-desktop');
                      break;
              }
          }


          function handleGridLayout(e) {
            if (!gridElement || !viewToggleContainer) return;

            if (e.matches) {
              resetToggleActiveState();
              
              if (currentGridPreference === 2) {
                updateGridClasses(2);
                viewToggleContainer.querySelector('button[data-columns="2"]').classList.add('active');
              } else {
                updateGridClasses(1);
                viewToggleContainer.querySelector('button[data-columns="1"]').classList.add('active');
              }
            } else {
              
              updateGridClasses(currentGridPreference);
              resetToggleActiveState();
              viewToggleContainer.querySelector(`button[data-columns="${currentGridPreference}"]`).classList.add('active');
            }
          }

          toggleButtons.forEach(button => {
              button.addEventListener('click', () => {
                  const newColumns = parseInt(button.dataset.columns, 10);
                  
                  currentGridPreference = newColumns;
                  
                  updateGridClasses(newColumns);

                  resetToggleActiveState();
                  button.classList.add('active');
              });
          });
          mediaQuery.addListener(handleGridLayout);
          handleGridLayout(mediaQuery);

    }, 1600)
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  
      setTimeout(function() {
       price_range_filter();
       initVariantSwatches();
       document.querySelectorAll('.view-toggle button').forEach(button => {
          button.addEventListener('click', function () {
            const cols = this.dataset.columns;
            const grid = document.getElementById('product-grid');
            grid.className = `grid product-grid grid--${cols}-col-desktop`;

            // Toggle active state
            document.querySelectorAll('.view-toggle button').forEach(btn => btn.classList.remove('active'));
            this.classList.add('active');
          });
        });
          // COLLECTION GRID VIEW FOR < 630PX 
          const gridElement = document.getElementById('product-grid');
          const viewToggleContainer = document.querySelector('.view-toggle');
          const toggleButtons = viewToggleContainer ? viewToggleContainer.querySelectorAll('button[data-columns]') : [];
          const mediaQuery = window.matchMedia('(max-width: 630px)');
          let currentGridPreference = 3;

          function resetToggleActiveState() {
              toggleButtons.forEach(button => button.classList.remove('active'));
          }

          function updateGridClasses(columns) {
              const classesToRemove = [
                  'grid--1-col-desktop', 
                  'grid--2-col-desktop', 'grid--2-col-tablet-down', 
                  'grid--3-col-desktop', 'grid--3-col-tablet-down', 
                  'grid--4-col-desktop', 'grid--4-col-tablet-down'
              ];
              gridElement.classList.remove(...classesToRemove);

              switch (columns) {
                  case 1:
                      gridElement.classList.add('grid--1-col-desktop');
                      break;
                  case 2:
                      gridElement.classList.add('grid--2-col-tablet-down', 'grid--2-col-desktop');
                      break;
                  case 3:
                      gridElement.classList.add('grid--3-col-tablet-down', 'grid--3-col-desktop');
                      break;
                  case 4:
                      gridElement.classList.add('grid--4-col-tablet-down', 'grid--4-col-desktop');
                      break;
              }
          }


          function handleGridLayout(e) {
            if (!gridElement || !viewToggleContainer) return;

            if (e.matches) {
              resetToggleActiveState();
              
              if (currentGridPreference === 2) {
                updateGridClasses(2);
                viewToggleContainer.querySelector('button[data-columns="2"]').classList.add('active');
              } else {
                updateGridClasses(1);
                viewToggleContainer.querySelector('button[data-columns="1"]').classList.add('active');
              }
            } else {
              
              updateGridClasses(currentGridPreference);
              resetToggleActiveState();
              viewToggleContainer.querySelector(`button[data-columns="${currentGridPreference}"]`).classList.add('active');
            }
          }

          toggleButtons.forEach(button => {
              button.addEventListener('click', () => {
                  const newColumns = parseInt(button.dataset.columns, 10);
                  
                  currentGridPreference = newColumns;
                  
                  updateGridClasses(newColumns);

                  resetToggleActiveState();
                  button.classList.add('active');
              });
          });

          mediaQuery.addListener(handleGridLayout);

          handleGridLayout(mediaQuery);
      }, 1600)

  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// price_range_filter()
function price_range_filter() {
  console.log('get here')
    const minRangeDesktop = document.getElementById("Desktop-Filter-price-range-min");
    const minRange = document.getElementById("Mobile-Filter-price-range-min");
      
    const maxRangeDesktop = document.getElementById("Desktop-Filter-price-range-max");
    const maxRange = document.getElementById("Mobile-Filter-price-range-max");
      
    // const minInput = document.getElementById("Mobile-Filter-Price-GTE");
    // const maxInput = document.getElementById("Mobile-Filter-Price-LTE");
    const minInput = document.querySelector(".price-input-min");
    const minInputMobile = document.querySelector(".mobile-price-input-min");
  
    const maxInput = document.querySelector(".price-input-max");
    const maxInputMobile = document.querySelector(".mobile-price-input-max");
  
    const rangeGroupDesktop = document.querySelector(".desktop-price-range-slider");
    const rangeGroup = document.querySelector(".price-range-slider");
  
    if (!minRange || !minRangeDesktop || !maxRange || !maxRangeDesktop || !minInput || !minInputMobile || !maxInput || !maxInputMobile || !rangeGroup ) return;
  
    console.log("test log");
    
  
    function updateRangeUI() {
        const minValue = parseInt(minRange.value);
        const minValueDesktop = parseInt(minRangeDesktop.value);
      
        const maxValue = parseInt(maxRange.value);
        const maxValueDesktop = parseInt(maxRangeDesktop.value);
      
        const minPercent = (minValue / parseInt(minRange.max)) * 100;
        const minPercentDesktop = (minValueDesktop / parseInt(minRangeDesktop.max)) * 100;
      
        const maxPercent = (maxValue / parseInt(maxRange.max)) * 100;
        const maxPercentDesktop = (maxValueDesktop / parseInt(maxRangeDesktop.max)) * 100;

        rangeGroup.style.setProperty("--range-min", `${minPercent}%`);
        rangeGroup.style.setProperty("--range-max", `${maxPercent}%`);
        rangeGroupDesktop.style.setProperty("--range-min", `${minPercentDesktop}%`);
        rangeGroupDesktop.style.setProperty("--range-max", `${maxPercentDesktop}%`);
      
        minInput.value = "€" + minValueDesktop + ".00";
        minInputMobile.value = "€" + minValue + ".00";
        maxInput.value = "€" + maxValueDesktop + ".00";
        maxInputMobile.value = "€" + maxValue + ".00";

         if (rangeGroupDesktop.style.getPropertyValue("--range-min") == "0%" &&  rangeGroupDesktop.style.getPropertyValue("--range-max") == "100%"){
          if(document.getElementById("desktop-custom-reset-btn")){
           document.getElementById("desktop-custom-reset-btn").classList.add("reset-disabled");
          }
         } else {
          if(document.getElementById("desktop-custom-reset-btn")){
           document.getElementById("desktop-custom-reset-btn").classList.remove("reset-disabled");
          }
         }
    
         if (rangeGroup.style.getPropertyValue("--range-min") == "0%" &&  rangeGroup.style.getPropertyValue("--range-max") == "100%"){
          if(document.getElementById("mobile-custom-reset-btn")){
           document.getElementById("mobile-custom-reset-btn").classList.add("reset-disabled");
          }
         } else {
          if(document.getElementById("mobile-custom-reset-btn")){
           document.getElementById("mobile-custom-reset-btn").classList.remove("reset-disabled");
          }
         }
      
    }

    function updateSliders() {
        minRange.value = minInput.value;
        minRange.value = minInputMobile.value;
      
        maxRange.value = maxInput.value;
        maxRange.value = maxInputMobile.value;
      
        updateRangeUI();
    }

    minRange.addEventListener("input", function () {
        if (parseInt(minRange.value) > parseInt(maxRange.value)) {
            minRange.value = maxRange.value;
        }
        updateRangeUI();
    });

    minRangeDesktop.addEventListener("input", function () {
        if (parseInt(minRangeDesktop.value) > parseInt(maxRangeDesktop.value)) {
            minRangeDesktop.value = maxRangeDesktop.value;
        }
        updateRangeUI();
    });

    maxRange.addEventListener("input", function () {
        if (parseInt(maxRange.value) < parseInt(minRange.value)) {
            maxRange.value = minRange.value;
        }
        updateRangeUI();
    });

    maxRangeDesktop.addEventListener("input", function () {
        if (parseInt(maxRangeDesktop.value) < parseInt(minRangeDesktop.value)) {
            maxRangeDesktop.value = minRangeDesktop.value;
        }
        updateRangeUI();
    });

    minInput.addEventListener("input", function () {
        if (parseInt(minInput.value) > parseInt(maxInput.value)) {
            minInput.value = maxInput.value;
        }
        updateSliders();
    });

    minInputMobile.addEventListener("input", function () {
        if (parseInt(minInputMobile.value) > parseInt(maxInputMobile.value)) {
            minInputMobile.value = maxInputMobile.value;
        }
        updateSliders();
    });

    maxInput.addEventListener("input", function () {
        if (parseInt(maxInput.value) < parseInt(minInput.value)) {
            maxInput.value = minInput.value;
        }
        updateSliders();
    });

    maxInputMobile.addEventListener("input", function () {
        if (parseInt(maxInputMobile.value) < parseInt(minInputMobile.value)) {
            maxInputMobile.value = minInputMobile.value;
        }
        updateSliders();
    });
    
    updateRangeUI(); // Initialize colors on load
}
// price_range_filter()
